import { ArrowRight, BookMarked, Brain, Clock3, Inbox, Sparkles, Target } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { request } from "../api";
import { ErrorCard, Loading } from "../components/AsyncState";
import { BookCard } from "../components/BookCard";
import { UploadBook } from "../components/UploadBook";
import type { Book, Dashboard } from "../types";
import { errorMessage, formatNumber } from "../utils";

export function DashboardPage() {
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [books, setBooks] = useState<Book[]>([]);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const [summary, library] = await Promise.all([
        request<Dashboard>("/api/dashboard"),
        request<Book[]>("/api/books")
      ]);
      setDashboard(summary);
      setBooks(library);
    } catch (reason) {
      setError(errorMessage(reason));
    }
  }, []);

  useEffect(() => void load(), [load]);

  if (error) return <ErrorCard message={error} retry={() => void load()} />;
  if (!dashboard) return <Loading label="正在整理今天的阅读" />;

  return (
    <div className="stack">
      <header className="page-heading page-heading--hero">
        <div>
          <span className="eyebrow"><Sparkles size={14} /> Aujourd’hui</span>
          <h1>今天，继续读下去。</h1>
          <p>先完成一段真实阅读，再处理途中遇到的词。</p>
        </div>
        <UploadBook compact onUploaded={(book) => setBooks((items) => [book, ...items])} />
      </header>

      {!dashboard.latest_assessment && (
        <section className="callout callout--assessment">
          <div className="callout__icon"><Brain size={25} /></div>
          <div>
            <span className="eyebrow">约 4 分钟</span>
            <h2>先估一个宽范围</h2>
            <p>做一次实验性词汇决策测试，让预习列表从更合理的位置开始。</p>
          </div>
          <Link className="primary-button" to="/assessment">开始测试 <ArrowRight size={17} /></Link>
        </section>
      )}

      <section className="metric-grid">
        <article><BookMarked size={20} /><strong>{dashboard.books}</strong><span>本书</span></article>
        <article><Brain size={20} /><strong>{formatNumber(dashboard.known_lexemes)}</strong><span>阅读认识</span></article>
        <article><Inbox size={20} /><strong>{formatNumber(dashboard.annotations_to_process)}</strong><span>条待整理</span></article>
        <article><Clock3 size={20} /><strong>{formatNumber(dashboard.recent_progress?.words_read_estimate)}</strong><span>最近阅读词数</span></article>
      </section>

      {dashboard.active_goal && (
        <section className="reading-goal-card">
          <div className="reading-goal-card__head">
            <Target size={20} />
            <span>当前阅读目标</span>
            <strong>{formatNumber(dashboard.active_goal.target_words)} 词</strong>
          </div>
          <h2>{dashboard.active_goal.title}</h2>
          <p>第 {dashboard.active_goal.start_spine_index + 1}–{dashboard.active_goal.end_spine_index + 1} 节</p>
          <Link className="primary-button" to={`/read/${dashboard.active_goal.book_id}`}>
            继续目标 <ArrowRight size={17} />
          </Link>
        </section>
      )}

      <section>
        <div className="section-heading">
          <div>
            <span className="eyebrow">Bibliothèque</span>
            <h2>最近的书</h2>
          </div>
          <Link className="text-link" to="/library">全部书籍 <ArrowRight size={15} /></Link>
        </div>
        {books.length ? (
          <div className="book-grid">{books.slice(0, 3).map((book) => <BookCard key={book.id} book={book} />)}</div>
        ) : (
          <div className="empty-state">
            <BookMarked size={32} />
            <h3>从一本真正想读的书开始</h3>
            <p>导入无 DRM 的法语 EPUB。你可以先阅读，也可以先分析全书。</p>
            <UploadBook onUploaded={(book) => setBooks([book])} />
          </div>
        )}
      </section>
    </div>
  );
}
